import { Avatar, AvatarFallback } from '@folio/components';
import { cn, initialsOf } from '@folio/lib';
import type { EmailThreadDto } from 'korint-sdk';
import { Paperclip } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ToAttachBadge } from '../GeneralInboxModule/ThreadIndicators';
import { InboxDate } from '../GeneralInboxModule/InboxDate';
import { counterpartOf, STRETCHED_BUTTON } from '../GeneralInboxModule/threadHelpers';
import { Text } from '@folio/components/ui/text';
import { emailsToAttach, hasAnyAttachment } from './policyEmailThreads';

export function PolicyThreadRow({
  thread,
  policyId,
  isSelected,
  onOpen,
}: {
  thread: EmailThreadDto;
  policyId: string;
  isSelected: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const { address: counterpartAddress, label: counterpartLabel } = counterpartOf(thread);

  return (
    <li
      className={cn(
        'relative flex items-center gap-3 border-b px-3 py-2 transition-colors last:border-b-0',
        isSelected ? 'bg-primary/[0.05]' : 'hover:bg-muted/70',
        thread.isArchived && 'text-muted-foreground',
      )}
      data-testid="policy-exchanges-thread"
      data-unread={thread.isUnread}
      aria-current={isSelected || undefined}
    >
      <span
        aria-hidden
        className={cn('size-1.5 shrink-0 rounded-full', thread.isUnread && 'bg-blue-500')}
      />
      <Avatar aria-hidden className={cn('size-7 shrink-0', thread.isArchived && 'opacity-50')}>
        <AvatarFallback className="bg-sky-100 text-[10px] font-bold text-slate-700">
          {initialsOf(counterpartLabel)}
        </AvatarFallback>
      </Avatar>
      <button type="button" className={cn('min-w-0 flex-1', STRETCHED_BUTTON)} onClick={onOpen}>
        <span className="flex items-center gap-2">
          <Text
            inline
            size="sm"
            truncate
            weight={thread.isUnread ? 'bold' : 'medium'}
            className="min-w-0"
            title={counterpartAddress}
          >
            {counterpartLabel}
          </Text>
          {thread.isUnread && <span className="sr-only">{t('generalInbox.unread')}</span>}
          {thread.isArchived && <span className="sr-only">{t('generalInbox.archived')}</span>}
          {thread.emailCount > 1 && (
            <span
              className="shrink-0 rounded bg-muted px-1.5 text-[11px] tabular-nums text-muted-foreground"
              aria-label={t('generalInbox.emailCount', { count: thread.emailCount })}
            >
              {thread.emailCount}
            </span>
          )}
          <span className="flex-1" />
          {hasAnyAttachment(thread) && (
            <Paperclip
              className="size-3.5 shrink-0 text-muted-foreground"
              aria-label={t('generalInbox.hasAttachments')}
            />
          )}
          <InboxDate
            className={cn(
              'shrink-0 text-xs tabular-nums',
              thread.isUnread ? 'font-semibold text-foreground' : 'text-muted-foreground',
            )}
            date={thread.date}
          />
        </span>
        <span className="mt-0.5 flex items-center gap-2">
          <Text inline truncate size="sm" className="min-w-0 flex-1">
            <span className={thread.isUnread ? 'font-semibold' : 'font-medium'}>
              {thread.subject || t('generalInbox.noSubject')}
            </span>
            {thread.preview && <span className="text-muted-foreground"> {thread.preview}</span>}
          </Text>
          {emailsToAttach(thread, policyId).length > 0 && <ToAttachBadge />}
        </span>
      </button>
    </li>
  );
}
